import { useEffect, useMemo, useState } from 'react';
import AssignActivityService from 'services/AssingActivityService';
import useCenters from './centers/UseCenters';
import useGroups from './groups/UseGroups';
import useCurrentUser from './useCurrentUser';
import useObservable from './UseObservable';

const ServiceInstace = AssignActivityService.getInstance();

interface IUseAssignActivity {
  autoFetchs?: boolean
}

interface RemoveActivityInterface {
  activityId: number;
  groupId: number;
  skillProgrammeId: number;
}

function useAssignActivity(
  { autoFetchs }: IUseAssignActivity = { autoFetchs: true },
) {
  const [centers = []] = useObservable(ServiceInstace.getCenters());
  const [centerConfiguration = []] = useObservable(ServiceInstace.getCenterConfiguration());
  const [onAssumingActivity, setOnAssumingActivity] = useState(false);
  const [onResultAssumingActivity, setOnResultAssumingActivity] = useState<any>({ success: false, data: {} });
  const { isTeacher } = useCurrentUser();
  const { activeCenter } = useCenters();
  const { groupList, fetchGroups } = useGroups();

  /**
   * We need to filter the assigned centers based in the role, directors and levels above have
   * access to all classrooms, however for teacher role it's a bit different.
   *
   */
  const assignedCentersList = useMemo(() => {
    /**
     * IMPORTANT NOTE!
     * This workaround removes all those groups where the current teacher is not assigned to.
     * By using the endpoint /v1/centers/shared/centers/{centerId}/teachers/groups [Client API],
     * We cannot use this endpoint at all because it doesn't have all necessary data, thats' why we need to use CORE API.
     *
     * Basically we get the classroom ids of the teacher (CLIENT API) and we compare them to the list of
     * groups from Core API and finally remove all those groups not included in CLIENT API side.
     *
     * Once we join the APIs we shouldn't have this issue anymore.
    */
    if (isTeacher) {
      return [...centers].map((center: any) => {
        const activeCenterId = Number(activeCenter.external_id); // CORE API
        const centerId = Number(center.id); // CLIENT API


        if (centerId === activeCenterId) {
          if (groupList?.length) {
            const groupIds = groupList.map((groupListItem: any) => Number(groupListItem.external_id));

            center.groups = center.groups.filter((group: any) => groupIds.includes(group.id));
          }
        }

        return center;
      });
    }

    return centers;
  }, [isTeacher, centers, activeCenter, groupList]);

  /**
   * We need to fetch groups in the section only when the user is a Teacher,
   * because we need to calculate assignedCentersList variable
   */
  useMemo(() => {
    if (activeCenter?.id && isTeacher) {
      fetchGroups(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCenter.id, isTeacher]);

  // onMounted
  useEffect(() => {
    if (autoFetchs) {
      ServiceInstace.fetchCenters();
      ServiceInstace.fetchCenterConfiguration();
    }
    // eslint-disable-next-line
  }, []);

  const scheduleActivity = (body: any) => {
    setOnAssumingActivity(true);
    setOnResultAssumingActivity({ success: false, data: body });
    return ServiceInstace.scheduleActivity(body)
      .then((response: any) => {
        setOnAssumingActivity(false);
        setOnResultAssumingActivity({ success: true, data: body });
        return response;
      }, (error: any) => {
        setOnAssumingActivity(false);
        setOnResultAssumingActivity({ success: false, data: body });
        return error;
      });
  };

  const removeActivity = (formData: RemoveActivityInterface) => {
    const { activityId, groupId, skillProgrammeId } = formData;

    return ServiceInstace.removeActivity(activityId, groupId, skillProgrammeId);
  };

  return {
    ServiceInstace,
    centers,
    centerConfiguration,
    scheduleActivity,
    onAssumingActivity,
    onResultAssumingActivity,
    removeActivity,
    assignedCentersList,
  };
}

export default useAssignActivity;
